/**
 * Auth-adjacent layout guards, extracted from `InspectorLayout.tsx` as a
 * pure structural move (effects byte-identical to the inline originals).
 *
 * - `useAuthRedirect` - the security barrier: once auth has resolved to
 *   "not authenticated", hard-redirect to /login with the current path
 *   encoded so the user lands back where they were.
 * - `useSmartAuthLoading` - delays the auth-loading spinner by 300ms when
 *   no valid auth cache exists, so a fast auth check never flashes a
 *   loading screen.
 */
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { hasValidAuthCache } from '../utils/authCache'
import { logger } from '../utils/logger'

export function useAuthRedirect({
  isAuthenticated,
  authLoading,
}: {
  isAuthenticated: boolean
  authLoading: boolean
}) {
  const location = useLocation()

  // SECURITY: Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      const redirectPath = location.pathname + location.search
      logger.debug('🔒 InspectorLayout: Not authenticated, redirecting to login')
      window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`
    }
  }, [isAuthenticated, authLoading, location])
}

export function useSmartAuthLoading(authLoading: boolean) {
  const [showAuthLoading, setShowAuthLoading] = useState(false)

  // Apply smart loading delay for auth loading
  useEffect(() => {
    if (authLoading && !hasValidAuthCache()) {
      const timer = setTimeout(() => {
        setShowAuthLoading(true)
      }, 300) // 300ms delay before showing auth loading

      return () => clearTimeout(timer)
    } else {
      setShowAuthLoading(false)
    }
  }, [authLoading])

  return showAuthLoading
}
